import mixpanel from 'mixpanel-browser';

import {
  ISuperKeys,
  IPeopleKeys,
  ISharedKeys
} from '../_shared/types/mixpanel';
import MixpanelConfig from '../_shared/constants/mixpanelConfig';
import { DealerType } from '../_shared/types/user';
import { TradeStateName, TradeContextName } from '../_shared/types/trade';
import ROUTES from '../components/Routes/constants';
import FilterService from './FilterService';
import { FilterType } from '../_shared/constants/filters';
import {
  RetailMediaType,
  RetailPlatforms,
  RETAIL_INPUTS
} from '../_shared/constants/retail';

const secretToken = Symbol('secretToken');

/**
 * TODO: Move this to extend Singleton (add ability to add different logic on init)
 */
class MixpanelService {
  private static instance: MixpanelService;

  private user;

  constructor(token: symbol) {
    if (token !== secretToken) {
      throw new Error('Constructor is private');
    }

    this.user = JSON.parse(window.localStorage.getItem('auth'))?.user;
  }

  static get(): MixpanelService {
    if (!this.instance) {
      this.instance = new MixpanelService(secretToken);

      mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
        cross_subdomain_cookie: false
      });
    }
    return this.instance;
  }

  // Set super and people properties for mixpanel from user
  setProperties() {
    const { user } = this;

    if (!user) {
      return;
    }

    if (!MixpanelService.isActive()) {
      return;
    }

    const isCSU = MixpanelService.isCSU(user);
    const userId = MixpanelService.getDistinctId(user);
    const timeStamp = MixpanelService.getTimeStamp();
    const userRoles = MixpanelService.pickRoles(user);
    const appVersion = MixpanelService.getCurrentAppVersion();

    const { peopleKeys } = MixpanelConfig.login;
    const { sharedKeys } = MixpanelConfig.login;
    const { superKeys } = MixpanelConfig.login;

    const ALL_KEYS = Object.assign(peopleKeys, sharedKeys, superKeys);
    const newProperties = {
      [ALL_KEYS.PLATFORM]: 'web',
      [ALL_KEYS.ENDPOINT]: process.env.REACT_APP_API_HOST,
      [ALL_KEYS.ENV]: process.env.REACT_APP_CUSTOM_NODE_ENV,
      [ALL_KEYS.CSU]: isCSU,
      [ALL_KEYS.LANGUAGE]: user.lang,
      [ALL_KEYS.REGION]: user.country.shortName,
      [ALL_KEYS.APP]: `TradeRev ${user.country.shortName}`,
      [ALL_KEYS.APP_VERSION]: appVersion,
      [ALL_KEYS.ISWEB5]: true,
      [ALL_KEYS.FIRSTNAME]: user.firstName,
      [ALL_KEYS.LASTNAME]: user.lastName,
      [ALL_KEYS.DEALER]: user.companyName,
      [ALL_KEYS.DEALER_GROUP]: user.dealerGroups,
      [ALL_KEYS.DEALER_ID]: user.dealerId,
      [ALL_KEYS.EMAIL]: user.email,
      [ALL_KEYS.ROLE]: userRoles,
      [ALL_KEYS.NAME]: user.fullName,
      [ALL_KEYS.LAST_LOGIN]: timeStamp,
      [ALL_KEYS.SUPERUSER_ID]: MixpanelService.calcDistinctId(
        user.country.shortName === 'CA'
          ? MixpanelConfig.regionCode.ca
          : MixpanelConfig.regionCode.ca,
        user.superUserId
      ),
      [ALL_KEYS.SUPERUSER_TYPE]: isCSU
        ? MixpanelConfig.login.userTypes.CLIENTSUPPORTUSER
        : MixpanelConfig.login.userTypes.UNIVERSALUSER,
      [ALL_KEYS.DEALER_REGION]: user.province,
      [ALL_KEYS.UUDEALER_ID]: user.universalUserDealerId,
      [ALL_KEYS.LAST_LOGIN_WEB]: timeStamp,
      [ALL_KEYS.DISTINCT_ID]: userId
    };

    const superProps = {};
    const peopleProps = {};

    // superKeys set
    const superKeySet = Object.keys(superKeys).map(key => {
      return superKeys[key];
    });

    const sharedKeySet = Object.keys(sharedKeys).map(key => {
      return sharedKeys[key];
    });

    Object.keys(newProperties).forEach(prop => {
      if (superKeySet.indexOf(prop) > -1) {
        superProps[prop] = newProperties[prop];
      } else {
        peopleProps[prop] = newProperties[prop];

        if (sharedKeySet.indexOf(prop) > -1) {
          superProps[prop] = newProperties[prop];
        }
      }
    });

    // reset mixpanel then set Super/People properties
    mixpanel.reset();
    mixpanel.identify(userId);
    MixpanelService.setSuperProperties(superProps);
    MixpanelService.setPeopleProperties(peopleProps);
  }

  static setSuperProperties(properties: ISuperKeys | ISharedKeys) {
    mixpanel.register(properties);
  }

  static setPeopleProperties(properties: IPeopleKeys | ISharedKeys) {
    mixpanel.people.set(properties);
  }

  /**
   * Determines if mixpanel is active
   */
  static isActive() {
    // eslint-disable-next-line no-underscore-dangle
    return mixpanel && mixpanel.__loaded && mixpanel.get_distinct_id();
  }

  /**
   * Track events on mixpanel
   */
  track(eventName: string, eventProperties: object = {}) {
    const filteredProperties = {};
    const userFromLocal = JSON.parse(window.localStorage.getItem('auth'))?.user;

    Object.keys(eventProperties).forEach(prop => {
      if (
        typeof eventProperties[prop] !== 'undefined' &&
        eventProperties[prop] !== ''
      ) {
        filteredProperties[prop] = eventProperties[prop];
      }
    });

    // check if mixpanel is active and initialized
    if (MixpanelService.isActive()) {
      // Check for localstorage if the user object is undefined
      if (!this.user && userFromLocal) {
        this.user = userFromLocal;
      }

      // check that the current Mixpanel properties are not stale
      if (MixpanelService.isStaleProps()) {
        this.setProperties();
      }
      mixpanel.track(`${eventName}`, filteredProperties);
    }
  }

  /**
   *
   */
  static isStaleProps() {
    const lastAppVersion = mixpanel.get_property(
      MixpanelConfig.login.superKeys.APP_VERSION
    );

    return lastAppVersion !== MixpanelService.getCurrentAppVersion();
  }

  /**
   * Register error details with mixpanel
   */
  static trackError() {}

  /**
   * Determines if user is a super user
   * NOTE since TR5 VDP page does not receive the superUser information
   * stored on TR4 there is a comparison on the user object to see if the user
   * id is equal to the super user id to determine if its a superuser
   */
  static isCSU(user) {
    return user.id !== user.superUserId;
  }

  /**
   * Returns the web app version
   */
  static getCurrentAppVersion(): string {
    return process.env.REACT_APP_GIT_TAG
      ? `${process.env.REACT_APP_CUSTOM_NODE_ENV}:${process.env.REACT_APP_GIT_TAG}`
      : '';
  }

  /**
   * Returns a distinct id for mixpanel
   */
  static calcDistinctId = (regionId, userId) => {
    return `${regionId}_${userId}`;
  };

  /**
   * Returns a unique user id
   */
  static getDistinctId(user) {
    const regionCode =
      user.country.shortName === 'CA'
        ? MixpanelConfig.regionCode.ca
        : MixpanelConfig.regionCode.us;

    if (user.superUserType && user.superUserType === DealerType.universalUser) {
      return MixpanelService.calcDistinctId(regionCode, user.superUserId);
    }
    if (user) {
      return MixpanelService.calcDistinctId(regionCode, user.id);
    }
    return '';
  }

  /**
   * Returns the current timestamp
   */
  static getTimeStamp() {
    return new Date().toISOString();
  }

  /**
   * Filters user app roles into a list
   */
  static pickRoles(user) {
    const roleList = [];
    const USER_ROLES = MixpanelConfig.login.userRoles;

    Object.keys(USER_ROLES).forEach(key => {
      if (user[key]) {
        roleList.push(USER_ROLES[key]);
      }
    });

    return roleList;
  }

  /**
   * Tracking functions
   */

  /**
   * Register event when the user opens multimedia gallery
   */
  openMultiMediaGallery(
    mmLandingSource: string,
    tradeState,
    tradeSection,
    tradeDetails,
    mediaList
  ) {
    if (!tradeDetails || !mediaList) {
      return;
    }

    const data = {
      trade_id: tradeDetails.id,
      auction_id: tradeDetails.auctionSummary?.id,
      vin: tradeDetails.vin,
      year: tradeDetails.year,
      make: tradeDetails.manufacturerName,
      model: tradeDetails.modelName,
      trim: tradeDetails.trim,
      as_is: tradeDetails.isAsIs,
      trade_state: TradeStateName[tradeState],
      trade_state_section: TradeContextName[tradeSection],
      disclosures: '',
      source: mmLandingSource,
      overview_images: mediaList.meta?.count?.photos,
      disclosure_images: mediaList.meta?.count?.damages,
      videos: mediaList.meta?.count?.videos,
      seller_dealer_name: tradeDetails.dealer.displayName,
      seller_dealer_id: tradeDetails.dealer.id
    };

    this.track(MixpanelConfig.names.openMultiMediaGallery, data);
  }

  /**
   * Tracks navigation activity
   */
  trackNavActivity(linkTo, context) {
    if (!linkTo) {
      return;
    }
    let data = {};
    let eventName = '';

    switch (linkTo) {
      case ROUTES.HOME:
        eventName = MixpanelConfig.names.homeNavigation;
        break;
      case ROUTES.BUYING:
        eventName = MixpanelConfig.names.buyingNavigation;
        break;
      case ROUTES.SELLING:
        eventName = MixpanelConfig.names.sellingNavigation;
        break;
      case ROUTES.RESEARCH:
        eventName = MixpanelConfig.names.researchNavigation;
        data = { research_option: 'navigation' };
        break;
      case ROUTES.CREATE:
        eventName = MixpanelConfig.names.createNavigation;
        data = { create_option: 'create trade' };
        break;
      default:
        break;
    }

    if (linkTo === ROUTES.BUYING_PURCHASE) {
      eventName = MixpanelConfig.names.navigateToDMPCheckoutFromNavbar;
    } else if (linkTo.startsWith(ROUTES.BUYING)) {
      eventName = MixpanelConfig.names.buyingNavigation;
      data = { buying_option: TradeStateName[context]?.toLowerCase() };
    }

    this.track(eventName, data);
  }

  /**
   * Track Filter for create and update
   */
  trackFilter(command: string, params) {
    let eventName = '';
    switch (command) {
      case 'update':
        eventName = MixpanelConfig.names.updateFilter;
        break;
      case 'create':
        eventName = MixpanelConfig.names.createFilter;
        break;
      default:
        return;
    }

    const data = {
      start_year: params?.yearRange?.start,
      end_year: params?.yearRange?.end,
      start_mileage: params?.mileageRange?.start,
      end_mileage: params?.mileageRange?.end,
      distance: FilterService.getSelectedItemsList(params?.distances),
      buyerAddresses: FilterService.getSelectedItemsList(
        params?.buyerAddresses
      ),
      location: FilterService.getSelectedItemsList(params?.locations),
      makes: FilterService.getSelectedItemsList(params?.manufacturers),
      models: FilterService.getSelectedItemsModels(params?.models),
      transmission: FilterService.getSelectedItemsList(
        params?.transmissionTypes
      ),
      fuel_types: FilterService.getSelectedItemsList(params?.fuelTypes),
      is_floor_met: params?.tradeConditions?.isFloorPriceMet,
      instock_only: params?.tradeConditions?.isInStock,
      franchise_only: params?.tradeConditions?.isFranchiseDealer,
      hide_as_is: params?.conditions[0]?.isSelected,
      hide_with_accident: params?.conditions[1]?.isSelected,
      hide_with_3000: params?.conditions[2]?.isSelected,
      trade_state: TradeStateName[params?.tradeState],
      list_type: params?.listType
    };

    this.track(eventName, data);
  }

  /**
   * Track filter for apply
   */
  trackFilterApply(params, listType) {
    const data = {} as any;
    data.list_type = listType;

    Object.entries(params).forEach(([key, value]: [string, any]) => {
      switch (key) {
        case FilterType.Location:
          data.location = FilterService.getSelectedItemsList(value);
          break;
        case FilterType.Distance:
          [data.distance] = FilterService.getSelectedItemsList(value);
          break;
        case FilterType.BuyerAddress:
          [data.buyer_address] = FilterService.getSelectedItemsList(value);
          break;
        case FilterType.YearRange:
          data.start_year = value?.start;
          data.end_year = value?.end;
          break;
        case FilterType.Manufacturer:
          data.makes = FilterService.getSelectedItemsList(value);
          break;
        case FilterType.Model:
          data.models = FilterService.getSelectedItemsModels(value);
          break;
        case FilterType.MileageRange:
          data.start_mileage = value?.start;
          data.end_mileage = value?.end;
          break;
        case FilterType.TransmissionType:
          data.transmission = FilterService.getSelectedItemsList(value);
          break;
        case FilterType.FuelType:
          data.fuel_types = FilterService.getSelectedItemsList(value);
          break;
        case FilterType.Condition:
          data.hide_as_is = value[0]?.isSelected;
          data.hide_with_accident = value[1]?.isSelected;
          data.hide_with_3000 = value[2]?.isSelected;
          break;
        case FilterType.TradeCondition:
          data.is_floor_met = value?.isFloorPriceMet;
          data.instock_only = value?.isInStock;
          data.franchise_only = value?.isFranchiseDealer;
          break;
        default:
      }
    });

    this.track(MixpanelConfig.names.applyFilter, data);
  }

  /**
   * Tracks a user selecting a filter from the filter list
   */
  trackSelectFilter(filterName, tradeState, listType) {
    this.track(MixpanelConfig.names.selectFilter, {
      filter_name: filterName,
      trade_state: TradeStateName[tradeState],
      list_type: listType
    });
  }

  /**
   * Tracks creating of Retail VDP from Customization page
   */
  trackRetailPublicCreate({
    guid,
    tradeDetails,
    retailMediaList,
    originalMediaList,
    retailInputRequest,
    isMobile,
    showDamages,
    showVideos
  }) {
    const totalOverviewImages = originalMediaList?.meta?.count?.photos || 0;
    const selectedOverviewImages =
      retailMediaList?.filter(
        ele => ele.mediaType === RetailMediaType.IMAGE && ele.isChecked === true
      )?.length || 0;
    const droppedOverviewImages = retailMediaList
      ?.filter(
        ele =>
          ele.mediaType === RetailMediaType.IMAGE && ele.isChecked === false
      )
      ?.map(({ label }) => label);
    const totalDamageImages = originalMediaList?.meta?.count?.damages || 0;
    const selectedDamageImages =
      retailMediaList?.filter(
        ele =>
          ele.mediaType === RetailMediaType.DAMAGE && ele.isChecked === true
      )?.length || 0;
    const droppedDamageImages = retailMediaList
      ?.filter(
        ele =>
          ele.mediaType === RetailMediaType.DAMAGE && ele.isChecked === false
      )
      ?.map(({ label }) => label);
    const totalVideos = originalMediaList?.meta?.count?.videos || 0;
    const selectedVideos =
      retailMediaList?.filter(
        ele => ele.mediaType === RetailMediaType.VIDEO && ele.isChecked === true
      )?.length || 0;

    this.track(MixpanelConfig.names.generateRetailVdp, {
      retail_guid: guid,
      retail_price: retailInputRequest[RETAIL_INPUTS.PRICE],
      retail_notes: !!retailInputRequest[RETAIL_INPUTS.NOTES],
      total_overview_images: totalOverviewImages,
      selected_overview_images: selectedOverviewImages,
      dropped_overview_images:
        droppedOverviewImages?.length > 0
          ? [...new Set(droppedOverviewImages)]
          : 'none',
      blur_background: false,
      include_damages_images: showDamages,
      total_damage_images: totalDamageImages,
      selected_damage_images: selectedDamageImages,
      dropped_damage_images:
        droppedDamageImages?.length > 0
          ? [...new Set(droppedDamageImages)]
          : 'none',
      include_videos: showVideos,
      total_videos: totalVideos,
      selected_videos: selectedVideos,
      dropped_videos: totalVideos - selectedVideos,
      sales_person_retail_vdp: !!retailInputRequest?.[RETAIL_INPUTS.NAME],
      sales_person_email_retail_vdp: !!retailInputRequest?.[
        RETAIL_INPUTS.EMAIL
      ],
      sales_person_phone_retail_vdp: !!retailInputRequest?.[
        RETAIL_INPUTS.PHONE
      ],
      dealer_website_retail_vdp: !!retailInputRequest?.[
        RETAIL_INPUTS.INVENTORY
      ],
      trade_id: tradeDetails.id,
      year: tradeDetails.year,
      make: tradeDetails.manufacturerName,
      model: tradeDetails.modelName,
      vin: tradeDetails.vin,
      trim: tradeDetails.trim,
      asIs: tradeDetails.isAsIs,
      trade_state: TradeStateName[tradeDetails.tradeState],
      platform: isMobile ? RetailPlatforms.Mobile : RetailPlatforms.Web,
      section: 'buy' // TODO: Figure out how to get Sell Section here
    });
  }
}

export default MixpanelService;
